/**
 * cart-gift.js  (Theme Asset – assets/cart-gift.js)
 * ═══════════════════════════════════════════════════════════════════════════
 * Verwaltet mehrere unabhängige Gratisartikel-Aktionen.
 * Jedes Geschenk hat eigene Trigger-Bedingungen und wird separat geprüft.
 *
 * Voraussetzungen:
 *   - window.cartGiftConfig gesetzt (via snippets/cart-gift-config.liquid)
 *   - PUB_SUB_EVENTS + subscribe/publish global verfügbar (Dawn Theme)
 * ═══════════════════════════════════════════════════════════════════════════
 */
(function () {
  'use strict';

  // ── Konfiguration prüfen ───────────────────────────────────────────────────
  const cfg = window.cartGiftConfig;
  if (!cfg || !cfg.enabled || !Array.isArray(cfg.gifts) || cfg.gifts.length === 0) {
    console.log('[Cart Gift] ⚠ Script inaktiv – Konfiguration fehlt oder deaktiviert:', cfg);
    return;
  }
  console.log('[Cart Gift] ✓ Gestartet –', cfg.gifts.filter(g => g.enabled).length, 'aktive Geschenk(e)');

  // Verhindert Endlosschleifen bei eigenen Cart-Updates
  let isProcessingGifts = false;

  // ── Hilfsfunktionen ────────────────────────────────────────────────────────

  function sameId(a, b) {
    return String(a) === String(b);
  }

  /** Prüft ob ein bestimmtes Geschenk im Warenkorb liegt. */
  function isGiftInCart(items, gift) {
    return items.some(
      (item) =>
        sameId(item.product_id, gift.giftProductId) ||
        sameId(item.variant_id, gift.giftVariantId)
    );
  }

  /** Prüft ob mindestens ein Trigger für dieses Geschenk erfüllt ist. */
  function shouldReceiveGift(items, gift) {
    return gift.triggers.some((trigger) => {
      if (!trigger.enabled) return false;

      const qty = items.reduce((total, item) => {
        let isMatch = false;
        if (trigger.type === 'product') {
          isMatch = sameId(item.product_id, trigger.productId);
        } else if (trigger.type === 'collection') {
          isMatch =
            Array.isArray(trigger.collectionProductIds) &&
            trigger.collectionProductIds.some((id) => sameId(id, item.product_id));
        }
        if (isMatch) {
          console.log('[Cart Gift]   → Trigger-Match:', item.title, '| Menge:', item.quantity, '/ min', trigger.minQuantity);
        }
        return isMatch ? total + item.quantity : total;
      }, 0);

      return qty >= trigger.minQuantity;
    });
  }

  /** Fügt ein Geschenk zum Warenkorb hinzu. */
  async function addGift(gift) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: gift.giftVariantId, quantity: 1 }),
    });
    if (!res.ok) throw new Error('[Cart Gift] Hinzufügen fehlgeschlagen: ' + res.status);
    return res.json();
  }

  /** Entfernt ein Geschenk per Line-Key (zuverlässiger als Variant-ID). */
  async function removeGift(items, gift) {
    const giftItem = items.find(
      (item) =>
        sameId(item.product_id, gift.giftProductId) ||
        sameId(item.variant_id, gift.giftVariantId)
    );
    if (!giftItem) throw new Error('[Cart Gift] Geschenk nicht im Cart gefunden');
    console.log('[Cart Gift] Entferne per Line-Key:', giftItem.key);
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
    });
    if (!res.ok) throw new Error('[Cart Gift] Entfernen fehlgeschlagen: ' + res.status);
    return res.json();
  }

  /** Setzt Gratisartikel-Menge auf 1 zurück falls Kunde sie erhöht hat. */
  async function enforceGiftQuantity(items, gift) {
    const giftItem = items.find(
      (item) =>
        sameId(item.product_id, gift.giftProductId) ||
        sameId(item.variant_id, gift.giftVariantId)
    );
    if (!giftItem || giftItem.quantity <= 1) return false;
    console.log('[Cart Gift] ⚠ Gratisartikel-Menge war', giftItem.quantity, '– setze auf 1 zurück');
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: giftItem.key, quantity: 1 }),
    });
    if (!res.ok) throw new Error('[Cart Gift] Mengenschutz fehlgeschlagen: ' + res.status);
    return true;
  }

  // ── UI: Mengensperre + Gratisaktion-Badge ──────────────────────────────────

  function applyGiftUI(items) {
    if (!items) return;

    // Alle aktiven Geschenk-Produkt-IDs sammeln
    const giftIds = cfg.gifts
      .filter((g) => g.enabled)
      .map((g) => ({ pid: String(g.giftProductId), vid: String(g.giftVariantId) }));

    items.forEach((item, index) => {
      const isGift = giftIds.some(
        (g) => sameId(item.product_id, g.pid) || sameId(item.variant_id, g.vid)
      );
      if (!isGift) return;

      const lineNum = index + 1;
      ['CartItem', 'CartDrawer-Item'].forEach((prefix) => {
        const el = document.getElementById(`${prefix}-${lineNum}`);
        if (!el) return;

        // Komplette Mengen-/Entfernen-Zelle ausblenden
        const qtyTd = el.querySelector('.cart-item__quantity');
        if (qtyTd) qtyTd.style.display = 'none';

        // Titellink deaktivieren
        const titleLink = el.querySelector('a.cart-item__name');
        if (titleLink && titleLink.getAttribute('href')) {
          titleLink.removeAttribute('href');
          titleLink.style.cursor = 'default';
          titleLink.style.pointerEvents = 'none';
        }

        // Preis "0,00 €" ausblenden + Gratisaktion-Badge (nur einmal)
        if (!el.querySelector('.cart-gift-badge')) {
          const priceEl = el.querySelector('.cart-item__details div.product-option');
          if (priceEl) {
            priceEl.style.display = 'none';
            const badge = document.createElement('div');
            badge.className = 'cart-gift-badge';
            badge.textContent = 'Gratisaktion';
            badge.style.cssText = [
              'color: #295529',
              'background: #f0f7f0',
              'font-size: 11px',
              'font-weight: 600',
              'padding: 2px 8px',
              'border-radius: 3px',
              'margin-top: 4px',
              'display: inline-block',
              'letter-spacing: 0.02em',
            ].join('; ');
            priceEl.insertAdjacentElement('afterend', badge);
          }
        }
      });
    });
  }

  // ── Hauptlogik: alle Geschenke prüfen ─────────────────────────────────────

  async function checkAllGifts(items, source) {
    if (isProcessingGifts) {
      console.log('[Cart Gift] ⏳ Wird bereits verarbeitet – überspringe Check');
      return;
    }

    const activeGifts = cfg.gifts.filter((g) => g.enabled && g.giftVariantId && g.giftProductId);
    if (activeGifts.length === 0) return;

    let anyChange = false;
    isProcessingGifts = true;

    try {
      for (const gift of activeGifts) {
        const inCart    = isGiftInCart(items, gift);
        const shouldGet = shouldReceiveGift(items, gift);

        console.log(`[Cart Gift] 📊 Geschenk ${gift.giftVariantId} (${source}):`, {
          shouldGet,
          inCart,
          aktion: shouldGet === inCart ? 'nichts zu tun' : (shouldGet ? 'HINZUFÜGEN' : 'ENTFERNEN'),
        });

        if (shouldGet && !inCart) {
          console.log('[Cart Gift] ➕ Füge Geschenk hinzu:', gift.giftVariantId);
          await addGift(gift);
          anyChange = true;
        } else if (!shouldGet && inCart) {
          console.log('[Cart Gift] ➖ Entferne Geschenk:', gift.giftVariantId);
          await removeGift(items, gift);
          anyChange = true;
        } else if (shouldGet && inCart) {
          // Mengenschutz: Gratisartikel darf nicht > 1 sein
          const clamped = await enforceGiftQuantity(items, gift);
          if (clamped) anyChange = true;
        }
      }

      if (anyChange) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift' });
      }
    } catch (err) {
      console.error('[Cart Gift] ✗ Fehler:', err);
    } finally {
      isProcessingGifts = false;
    }
  }

  // ── Frischen Cart-Stand holen und prüfen ───────────────────────────────────

  function fetchAndCheck(source) {
    fetch('/cart.js', { cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => {
        checkAllGifts(cart.items, source);
        applyGiftUI(cart.items);
      })
      .catch((e) => console.error('[Cart Gift] Fetch-Fehler:', e));
  }

  // ── MutationObserver: UI-Lock nach jedem Cart-Re-Render wiederherstellen ────
  // Dawn ersetzt den kompletten Cart-HTML bei jedem Update – dadurch gehen
  // JS-Styles verloren. Der Observer erkennt neue Cart-Rows und sperrt sie neu.

  let uiLockTimer = null;

  function setupMutationObserver() {
    const cartRoot =
      document.querySelector('cart-drawer') ||
      document.querySelector('.cart-drawer') ||
      document.getElementById('CartDrawer');

    if (!cartRoot) return;

    const observer = new MutationObserver((mutations) => {
      // Nur reagieren wenn echte Cart-Elemente hinzugefügt wurden
      // (nicht auf unsere eigenen Badge/Style-Änderungen)
      const hasNewCartNodes = mutations.some((m) =>
        m.type === 'childList' &&
        Array.from(m.addedNodes).some(
          (n) => n.nodeType === 1 && !n.classList.contains('cart-gift-badge')
        )
      );
      if (!hasNewCartNodes) return;

      clearTimeout(uiLockTimer);
      uiLockTimer = setTimeout(() => {
        fetch('/cart.js', { cache: 'no-store' })
          .then((r) => r.json())
          .then((cart) => applyGiftUI(cart.items))
          .catch(() => {});
      }, 100);
    });

    observer.observe(cartRoot, { childList: true, subtree: true });
    console.log('[Cart Gift] 👁 MutationObserver aktiv auf', cartRoot.tagName || cartRoot.id);
  }

  // ── Event-Subscription (PubSub) ────────────────────────────────────────────

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'cart-gift') return;
    console.log('[Cart Gift] 🔔 cartUpdate Event (source:', event.source, ')');
    fetchAndCheck('PubSub-Event');
  });

  // ── Fallback: direkt auf Mengen-Eingaben reagieren ─────────────────────────

  document.addEventListener('change', function (e) {
    if (!e.target.classList.contains('quantity__input')) return;
    setTimeout(() => fetchAndCheck('DOM-Change-Fallback'), 800);
  });

  // ── Beim Seitenladen prüfen + Observer starten ─────────────────────────────

  function init() {
    fetchAndCheck('Seitenladen');
    setupMutationObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();